import { ErrorMessage, Field, Form, Formik, FormikActions, FormikProps } from 'formik'
import { useEffect, useState } from 'react'
import toastr from 'toastr'
import * as Yup from 'yup'

import { CodeMap, CodeMapCategory, SelectCodeMapField } from 'adminConsole/page'
import { codeMapStore, emptyCodeMap } from 'adminConsole/store'
import { Loading } from 'mobileApp/common/page'
import { useHistory } from 'react-router'
import { removeObjEmptyVal } from 'share/util/helper'

type Props = {
  isAddMode: boolean
  id: number
}

export const EditForm = ({ isAddMode, id }: Props) => {
  const [isLoading, setIsLoading] = useState(false)

  const initialValues = isAddMode ? { ...emptyCodeMap } : codeMapStore.codeMap

  const navigate = useHistory()

  const onSubmit = (values: CodeMap) => {
    const valueRemoveEmpty = removeObjEmptyVal(values)

    return isAddMode ? codeMapStore.addCodeMap(valueRemoveEmpty) : codeMapStore.updateCodeMap(valueRemoveEmpty)
  }
  const onDelete = () => {
    codeMapStore.deleteCodeMap(id)
    toastr.success('刪除成功')
    navigate.goBack()
  }
  const onCancel = () => navigate.goBack()

  useEffect(() => {
    Promise.all([codeMapStore.getCodeMapList(), !isAddMode && codeMapStore.getCodeMapById(id)])
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false))

    setIsLoading(true)
  }, [id, isAddMode])

  if (isLoading) return <Loading />

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        category: Yup.string().trim().required('必填欄位！'),
        name: Yup.string().trim().required('必填欄位！').max(50, '字數不可超過 50 字！'),
        value: Yup.string().trim().required('必填欄位！').max(50, '字數不可超過 50 字！'),
        enable: Yup.boolean().required('必填欄位！'),
        precedence: Yup.number(),
        reserve1: Yup.string().trim().nullable().max(500, '字數不可超過 500字 ！'),
        reserve2: Yup.string().trim().nullable().max(500, '字數不可超過 500字 ！'),
      })}
      onSubmit={(values: CodeMap, { setStatus, setSubmitting }: FormikActions<CodeMap>) => {
        setStatus()
        onSubmit(values)
          .then(() => {
            toastr.success(isAddMode ? '新增成功' : '編輯成功')
            navigate.goBack()
          })
          .catch(({ message }) => {
            setStatus(message)
            setSubmitting(false)
          })
      }}>
      {formikProps => (
        <CodeMapForm formikProps={formikProps} onCancel={onCancel} onDelete={onDelete} isAddMode={isAddMode} />
      )}
    </Formik>
  )
}

interface CodeMapFormProps {
  formikProps: FormikProps<CodeMap>
  onCancel: () => void
  onDelete: () => void
  isAddMode: boolean
}

const CodeMapForm = ({ formikProps, onCancel, onDelete, isAddMode }: CodeMapFormProps) => {
  const { errors, touched, status, isSubmitting } = formikProps

  return (
    <Form>
      <div className="row">
        <div className="form-group col-12 col-sm-6">
          <Field
            name="category"
            label="類型"
            category={CodeMapCategory.ListCodeMapCategory}
            codeMapList={codeMapStore.codeMapList}
            component={SelectCodeMapField}
          />
        </div>

        <div className="form-group col-12 col-sm-6">
          <label htmlFor="enable">狀態</label>
          <Field
            component="select"
            name="enable"
            className={'form-control' + (errors.enable && touched.enable ? ' is-invalid' : '')}>
            <option value="true">啟用</option>
            <option value="false">停用</option>
          </Field>
          <ErrorMessage name="enable" component="div" className="invalid-feedback" />
        </div>

        <div className="form-group col-12 col-sm-6">
          <label htmlFor="name">描述</label>
          <Field
            type="text"
            name="name"
            className={'form-control' + (errors.name && touched.name ? ' is-invalid' : '')}
          />
          <ErrorMessage name="name" component="div" className="invalid-feedback" />
        </div>

        <div className="form-group col-12 col-sm-6">
          <label htmlFor="precedence">順序</label>
          <Field
            type="number"
            name="precedence"
            className={'form-control' + (errors.precedence && touched.precedence ? ' is-invalid' : '')}
          />
          <ErrorMessage name="precedence" component="div" className="invalid-feedback" />
        </div>

        <div className="form-group col-12 col-sm-6">
          <label htmlFor="value">值</label>
          <Field
            type="text"
            name="value"
            className={'form-control' + (errors.value && touched.value ? ' is-invalid' : '')}
          />
          <ErrorMessage name="value" component="div" className="invalid-feedback" />
        </div>

        <div className="form-group col-12">
          <label htmlFor="reserve1">保留1</label>
          <Field
            component="textarea"
            name="reserve1"
            className={'form-control' + (errors.reserve1 && touched.reserve1 ? ' is-invalid' : '')}
            rows="3"
          />
          <ErrorMessage name="reserve1" component="div" className="invalid-feedback" />
        </div>

        <div className="form-group col-12">
          <label htmlFor="reserve2">保留2</label>
          <Field
            component="textarea"
            name="reserve2"
            className={'form-control' + (errors.reserve2 && touched.reserve2 ? ' is-invalid' : '')}
            rows="3"
          />
          <ErrorMessage name="reserve2" component="div" className="invalid-feedback" />
        </div>

        <div className="col-12 text-danger">{status}</div>
      </div>

      <section className="row mt-3">
        <div className="col-12 d-flex justify-content-between">
          <button
            type="button"
            className={`btn btn-danger ${isAddMode && 'invisible'}`}
            onClick={() => onDelete()}
            disabled={isSubmitting}>
            刪除
          </button>
          <div>
            <button type="button" className="btn btn-light mr-4" onClick={() => onCancel()} disabled={isSubmitting}>
              取消
            </button>
            <button type="submit" className="btn btn-secondary" disabled={isSubmitting}>
              儲存
            </button>
          </div>
        </div>
      </section>
    </Form>
  )
}
